import * as z from "zod";
import {
  DEFAULT_WAREHOUSE_GRADE_NAMES,
  type GradeMapping,
  GradeMappingSchema,
  resolveWarehouseGradeNames,
} from "./grade-mapping";
import {
  changedTeamPatchPaths,
  groupedSettingsChanges,
} from "./grouped-settings-changes";
import type { GradeBinMapping } from "./redo-managed-returns-settings";

const GradeBinMappingSchema = z.object({
  gradeName: z.string(),
  storageLocationId: z.string(),
});

export const ReturnsWarehouseSettingsSchema = z.object({
  grades: z.object({
    customGradesEnabled: z.boolean(),
    gradeMappings: z.array(GradeMappingSchema),
  }),
  bins: z.object({ gradeBinMappings: z.array(GradeBinMappingSchema) }),
});

export type ReturnsWarehouseSettings = z.infer<
  typeof ReturnsWarehouseSettingsSchema
>;

const settingsShape = ReturnsWarehouseSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsWarehouseSettingsChangesSchema = z.object({
  grades: settingsShape.grades.partial().optional(),
  bins: settingsShape.bins.partial().optional(),
});

export type ReturnsWarehouseSettingsChanges = z.infer<
  typeof ReturnsWarehouseSettingsChangesSchema
>;

/** The slice of `team.settings` the Warehouse tab reads. Structurally matches both the client team and the server's raw document. */
export interface ReturnsWarehouseSettingsSource {
  returnItemDisposition?: {
    enabled?: boolean | null;
    gradeOptions?: string[] | null;
  } | null;
  redoManagedReturns?: {
    gradeMappings?: GradeMapping[] | null;
    gradeBinMappings?: GradeBinMapping[] | null;
  } | null;
}

export function returnsWarehouseSettingsChanges(
  initial: ReturnsWarehouseSettings,
  next: ReturnsWarehouseSettings,
): ReturnsWarehouseSettingsChanges {
  return ReturnsWarehouseSettingsChangesSchema.parse(
    groupedSettingsChanges(initial, next),
  );
}

export function returnsWarehouseSettingsFromTeam(
  settings: ReturnsWarehouseSettingsSource,
): ReturnsWarehouseSettings {
  return {
    grades: {
      customGradesEnabled: !!settings.returnItemDisposition?.enabled,
      gradeMappings: settings.redoManagedReturns?.gradeMappings ?? [],
    },
    bins: {
      gradeBinMappings: settings.redoManagedReturns?.gradeBinMappings ?? [],
    },
  };
}

/** Turning custom grades on with none defined starts from the default A–E grades. */
function customGradeOptions(
  settings: ReturnsWarehouseSettingsSource,
  customGradesEnabled: boolean,
): string[] {
  const stored = settings.returnItemDisposition?.gradeOptions ?? [];
  return customGradesEnabled && stored.length === 0
    ? [...DEFAULT_WAREHOUSE_GRADE_NAMES]
    : stored;
}

/** The grades the warehouse assigns once `customGradesEnabled` is saved. */
export function warehouseGradeNames(
  settings: ReturnsWarehouseSettingsSource,
  customGradesEnabled: boolean,
): string[] {
  return resolveWarehouseGradeNames({
    customGradesEnabled,
    gradeOptions: customGradeOptions(settings, customGradesEnabled),
  });
}

function returnsWarehouseSettingsToTeamPatch(
  settings: ReturnsWarehouseSettingsSource,
  value: ReturnsWarehouseSettings,
): { set: Record<string, unknown>; unset: string[] } {
  const { customGradesEnabled } = value.grades;
  const gradeOptions = customGradeOptions(settings, customGradesEnabled);
  const set: Record<string, unknown> = settings.returnItemDisposition
    ? {
        "returnItemDisposition.enabled": customGradesEnabled,
        "returnItemDisposition.gradeOptions": gradeOptions,
      }
    : {
        returnItemDisposition: {
          enabled: customGradesEnabled,
          gradeOptions,
          outcomeOptions: [],
        },
      };
  // Grade actions and bins belong to managed returns; a team without it has nowhere to keep them.
  // Mappings for grades outside the saved set are dropped here, not while editing, so toggling custom grades back keeps them.
  if (settings.redoManagedReturns) {
    const gradeNames = new Set(
      warehouseGradeNames(settings, customGradesEnabled),
    );
    set["redoManagedReturns.gradeMappings"] = value.grades.gradeMappings.filter(
      (m) => gradeNames.has(m.gradeName),
    );
    set["redoManagedReturns.gradeBinMappings"] =
      value.bins.gradeBinMappings.filter((m) => gradeNames.has(m.gradeName));
  }
  return { set, unset: [] };
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere.
 */
export function returnsWarehouseTeamPatchForChanges(
  settings: ReturnsWarehouseSettingsSource,
  changes: ReturnsWarehouseSettingsChanges,
): { set: Record<string, unknown>; unset: string[] } {
  const storedValue = returnsWarehouseSettingsFromTeam(settings);
  const next: ReturnsWarehouseSettings = {
    grades: { ...storedValue.grades, ...changes.grades },
    bins: { ...storedValue.bins, ...changes.bins },
  };
  return changedTeamPatchPaths(
    returnsWarehouseSettingsToTeamPatch(settings, storedValue),
    returnsWarehouseSettingsToTeamPatch(settings, next),
  );
}
